import React, { useEffect, useRef, useState } from "react";
import { getLocalStorage } from "../../utils";
import { STORAGE_KEYS } from "../../constants";
import { systemHealthUrl } from "../constants";

/**
 * HealthBanner — a small, live system-load indicator (Isaac's idea, 2026-07-10).
 *
 * Polls /api/v1/system-health/ and shows a thin banner ONLY when the system is under load
 * (busy / strained / degraded). When everything's "ok" it renders nothing, so it never
 * clutters normal use — it appears exactly when you'd want to know (e.g. during a stress
 * test, or if the DB is nearing its connection limit).
 *
 * Deliberately does NOT use apiRequest: that helper redirects to /login on a 401, which would
 * hijack logged-out pages. This polls with a plain fetch, only when a token exists, and fails
 * silently on any error — the banner is a nice-to-have, never a way to break the app.
 */

type HealthStatus = "ok" | "busy" | "strained" | "degraded";

interface HealthResponse {
  status: HealthStatus;
  queue: { waiting: number | null; background_pending: number };
  db: { connections: number | null; limit: number; percent: number | null; ok: boolean };
}

const POLL_MS = 15000;

const STYLES: Record<Exclude<HealthStatus, "ok">, { bg: string; label: string }> = {
  busy: { bg: "#8a6d00", label: "Heavy load — new videos may take a little longer than usual." },
  strained: { bg: "#8a1c1c", label: "System under high load — near capacity, expect slower processing." },
  degraded: { bg: "#4a4a55", label: "A background service is unreachable — some updates may lag." },
};

const HealthBanner: React.FC = () => {
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      const token = getLocalStorage(STORAGE_KEYS.TOKEN);
      if (!token) {
        // not logged in — nothing to show, don't hit the endpoint (it needs auth)
        if (!cancelled) setHealth(null);
        return;
      }
      // Don't poll inside the embedded iframe editor (same-origin second instance).
      if (typeof window !== "undefined" && window.self !== window.top) return;
      try {
        const res = await fetch(systemHealthUrl, {
          credentials: "include",
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return; // ignore 401/5xx — never redirect, never surface
        const data: HealthResponse = await res.json();
        if (!cancelled) setHealth(data);
      } catch (e) {
        // transient/offline — leave the last state; the banner is best-effort
      }
    };

    poll();
    timer.current = setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      if (timer.current) clearInterval(timer.current);
    };
  }, []);

  if (!health || health.status === "ok") return null;

  const style = STYLES[health.status];
  const waiting = health.queue?.waiting;
  const dbPct = health.db?.percent;

  return (
    <div
      role="status"
      style={{
        position: "fixed",
        // A page can push the banner down below its own fixed top bar (the archive bar sets this).
        top: "var(--cc-banner-top, 0px)",
        left: 0,
        right: 0,
        zIndex: 2000,
        background: style.bg,
        color: "#fff",
        padding: "6px 14px",
        fontSize: 13,
        fontWeight: 600,
        textAlign: "center",
        letterSpacing: 0.2,
        boxShadow: "0 1px 6px rgba(0,0,0,0.35)",
      }}
    >
      {style.label}
      {(waiting != null && waiting > 0) || (dbPct != null && dbPct >= 60) ? (
        <span style={{ opacity: 0.75, fontWeight: 400, marginLeft: 8 }}>
          {waiting != null && waiting > 0 ? `${waiting} waiting` : ""}
          {waiting != null && waiting > 0 && dbPct != null && dbPct >= 60 ? " · " : ""}
          {dbPct != null && dbPct >= 60 ? `DB ${dbPct}%` : ""}
        </span>
      ) : null}
    </div>
  );
};

export default HealthBanner;
